/**
 * "Who can see this" — one control for dashboards and presentations.
 *   Only me · Team members (pick) · All team
 * Owner-only to change; everyone sees the current setting.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Lock, Users, Globe, ChevronDown, Check, Loader2 } from 'lucide-react';
import { coworkApi, type Visibility } from '@/cowork/omniad/api/coworkApi';

export interface VisibilityValue { visibility: Visibility; allowedUserIds: string[] }

const OPTIONS: Array<{ id: Visibility; label: string; hint: string; Icon: typeof Lock }> = [
  { id: 'private', label: 'Only me', hint: 'Nobody else on the team sees it.', Icon: Lock },
  { id: 'members', label: 'Team members', hint: 'Pick who can see it.', Icon: Users },
  { id: 'team', label: 'All team', hint: 'Everyone on the team.', Icon: Globe },
];

export function visibilityLabel(v: VisibilityValue | Visibility | undefined, count?: number): string {
  const vis = typeof v === 'string' ? v : v?.visibility || 'team';
  if (vis === 'private') return 'Only me';
  if (vis === 'members') { const n = count ?? (typeof v === 'object' ? v?.allowedUserIds?.length : 0) ?? 0; return n ? `${n} member${n === 1 ? '' : 's'}` : 'Team members'; }
  return 'All team';
}

export function VisibilityControl({ value, onChange, canEdit = true, saving, size = 'md', unowned, onClaim }: { value: VisibilityValue; onChange: (v: VisibilityValue) => void; canEdit?: boolean; saving?: boolean; /** Legacy row with no owner: explain that choosing a setting claims it, and offer a plain claim. */ unowned?: boolean; onClaim?: () => Promise<unknown> | void; size?: 'sm' | 'md' }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<VisibilityValue>(value);
  const [filter, setFilter] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const members = useQuery({ queryKey: ['cowork', 'team', 'members'], queryFn: () => coworkApi.team.members(), staleTime: 10 * 60_000, enabled: open });
  useEffect(() => { setDraft(value); }, [value.visibility, value.allowedUserIds.join(',')]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const current = OPTIONS.find((o) => o.id === value.visibility) || OPTIONS[2];
  const Icon = current.Icon;
  const apply = () => {
    if (draft.visibility === 'members' && !draft.allowedUserIds.length) return;
    onChange({ visibility: draft.visibility, allowedUserIds: draft.visibility === 'members' ? draft.allowedUserIds : [] });
    setOpen(false);
  };
  const toggleMember = (id: string) => setDraft((d) => ({ ...d, allowedUserIds: d.allowedUserIds.includes(id) ? d.allowedUserIds.filter((x) => x !== id) : [...d.allowedUserIds, id] }));
  const list = (members.data?.members || []).filter((m) => m.userId !== members.data?.me && (!filter.trim() || m.name.toLowerCase().includes(filter.toLowerCase()) || (m.email || '').toLowerCase().includes(filter.toLowerCase())));
  const pad = size === 'sm' ? 'px-2 py-1 text-[11.5px]' : 'px-3 py-1.5 text-[12px]';
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => canEdit && setOpen((o) => !o)} disabled={!canEdit} title={canEdit ? 'Who can see this' : 'Only the person who created it can change who sees it'} className={`flex items-center gap-1.5 rounded-lg border border-[#d0d5dd] ${pad} font-medium text-[#344054] hover:bg-[#f9fafb] disabled:cursor-default disabled:opacity-70`}>
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />} {visibilityLabel(value)} {canEdit && <ChevronDown className="h-3 w-3 text-[#98a2b3]" />}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1.5 w-72 rounded-xl border border-[#eaecf0] bg-white p-2 shadow-xl">
          {unowned && (
            <div className="mb-1.5 rounded-lg bg-[#fffaeb] px-2.5 py-2 text-[11px] text-[#93370d]">
              <div className="font-semibold">No owner yet</div>
              <div>This was made before ownership existed. Choosing a setting below makes you the owner.</div>
              {onClaim && <button type="button" onClick={() => { void onClaim(); setOpen(false); }} className="mt-1 font-semibold underline">Make me the owner without changing who sees it</button>}
            </div>
          )}
          {OPTIONS.map((o) => (
            <button key={o.id} type="button" onClick={() => setDraft((d) => ({ ...d, visibility: o.id }))} className={`flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[#f9fafb] ${draft.visibility === o.id ? 'bg-[#f4ecff]' : ''}`}>
              <o.Icon className={`mt-0.5 h-4 w-4 ${draft.visibility === o.id ? 'text-[#7c3aed]' : 'text-[#667085]'}`} />
              <span className="min-w-0 flex-1"><span className="block text-[12.5px] font-semibold text-[#101828]">{o.label}</span><span className="block text-[11px] text-[#667085]">{o.hint}</span></span>
              {draft.visibility === o.id && <Check className="h-4 w-4 text-[#7c3aed]" />}
            </button>
          ))}
          {draft.visibility === 'members' && (
            <div className="mt-1 border-t border-[#eaecf0] pt-2">
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find a team member…" className="mb-1.5 w-full rounded-md border border-[#d0d5dd] px-2 py-1 text-[12px]" />
              <div className="max-h-44 overflow-y-auto">
                {members.isPending && <div className="flex items-center gap-2 px-2 py-2 text-[11.5px] text-[#98a2b3]"><Loader2 className="h-3 w-3 animate-spin" /> Loading team…</div>}
                {members.error && <div className="px-2 py-2 text-[11.5px] text-[#b42318]">Could not load the team: {(members.error as Error).message}</div>}
                {list.map((m) => { const on = draft.allowedUserIds.includes(m.userId); return (
                  <label key={m.userId} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-[#f9fafb]">
                    <input type="checkbox" checked={on} onChange={() => toggleMember(m.userId)} className="accent-[#7c3aed]" />
                    <span className="min-w-0 flex-1 truncate text-[12px] text-[#101828]">{m.name}</span>
                    {m.role && <span className="text-[10px] uppercase tracking-wide text-[#98a2b3]">{m.role.replace(/_/g, ' ').toLowerCase()}</span>}
                  </label>
                ); })}
                {members.data && !list.length && <div className="px-2 py-2 text-[11.5px] text-[#98a2b3]">No one matches.</div>}
              </div>
              {!draft.allowedUserIds.length && <div className="px-2 pt-1 text-[11px] text-[#b54708]">Pick at least one member.</div>}
            </div>
          )}
          <div className="mt-2 flex justify-end gap-1.5 border-t border-[#eaecf0] pt-2">
            <button type="button" onClick={() => { setDraft(value); setOpen(false); }} className="rounded-md px-2.5 py-1 text-[12px] text-[#667085] hover:bg-[#f2f4f7]">Cancel</button>
            <button type="button" onClick={apply} disabled={draft.visibility === 'members' && !draft.allowedUserIds.length} className="rounded-md bg-[#be43ff] px-3 py-1 text-[12px] font-semibold text-white hover:bg-[#a835e0] disabled:opacity-50">Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}
